import React, { useEffect, useMemo, useRef, useState } from "react";
import { sentenceCaseLabel, useTranslation } from "../i18n";

import DataTableLoadingRow from "../Components/DataTableLoadingRow";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getInquiryListAction } from "../actions/inquiry";
import { backendUrl } from "../Router/constant";

const getValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const getLabel = (value, keys = ["name", "title", "label", "username"]) => {
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (!value || typeof value !== "object") return "";
  const label = getValue(...keys.map((key) => value[key]));
  return label === undefined ? "" : String(label);
};

const normalizeStatus = (status) =>
  String(status || "").trim().toLowerCase().replace(/[\s-]+/g, "_");

const getStatusFilterValue = (status) => {
  const value = normalizeStatus(status);
  if (value === "rejected") return "reject";
  if (value === "completed") return "complete";
  return value;
};

const formatStatus = (status) => sentenceCaseLabel(status || "Unknown");

const getImageUrl = (reference) => {
  if (reference && typeof reference === "object") {
    reference = getValue(
      reference.url,
      reference.secure_url,
      reference.src,
      reference._id,
      reference.id,
    );
  }
  if (!reference || typeof reference !== "string") return "/assets/inquiries-icon.svg";
  if (/^(https?:)?\/\//i.test(reference)) return reference;
  if (reference.startsWith("/")) {
    return reference.startsWith("/uploads/") ? backendUrl + reference : reference;
  }
  if (reference.includes("/")) return backendUrl + "/" + reference;
  return backendUrl + "/uploads/" + encodeURIComponent(reference);
};

const getInquiryRow = (item, index) => {
  const rawInquiryReference = item.inquiryId;
  const displayInquiryIdCandidate = getValue(
    typeof rawInquiryReference === "object"
      ? rawInquiryReference.inquiryId || rawInquiryReference.displayInquiryId || rawInquiryReference.referenceNumber || rawInquiryReference.code
      : rawInquiryReference,
    item.displayInquiryId,
    item.inquiry?.inquiryId,
    item.inquiry?.displayInquiryId,
    item.inquiry?.referenceNumber,
    item.inquiry?.code,
    item.referenceNumber,
    item.code,
    item.id,
  );
  const displayInquiryId =
    displayInquiryIdCandidate && !/^[a-f0-9]{24}$/i.test(String(displayInquiryIdCandidate).trim())
      ? String(displayInquiryIdCandidate)
      : "—";
  const inquiryDatabaseId = String(
    getValue(
      item._id,
      item.id,
      item.inquiry?._id,
      typeof rawInquiryReference === "object" ? rawInquiryReference._id : undefined,
      displayInquiryIdCandidate,
      "inquiry-" + index,
    ),
  );
  const requirements = Array.isArray(item.requirements) ? item.requirements : [];
  let productEntries = requirements.flatMap((requirement, requirementIndex) => {
    const products = Array.isArray(requirement.productIds)
      ? requirement.productIds
      : getValue(requirement.product, requirement.productId)
        ? [getValue(requirement.product, requirement.productId)]
        : [];

    return products.map((product, productIndex) => ({
      requirement,
      product,
      key: String(
        getValue(product?._id, product?.id, product?.productId, productIndex),
      ) + "-" + requirementIndex,
    }));
  });

  if (!productEntries.length) {
    const fallbackProducts = Array.isArray(item.products)
      ? item.products
      : [
          getValue(
            item.product,
            item.productId && typeof item.productId === "object"
              ? item.productId
              : undefined,
            item.commodity,
            item,
          ),
        ];
    productEntries = fallbackProducts.map((product, productIndex) => ({
      requirement: item,
      product,
      key: String(getValue(product?._id, product?.id, productIndex)),
    }));
  }

  const products = productEntries.map(({ requirement, product, key }) => {
    const sector = getValue(
      requirement.sectorId,
      requirement.sector,
      product?.sector,
      item.sector,
    );
    const sectorName =
      getLabel(sector, ["name", "title", "label"]) ||
      getValue(requirement.sectorName, product?.sectorName, item.sectorName) ||
      "—";
    const quantity = getValue(
      requirement.requestedQuantity,
      requirement.quantity,
      requirement.volume,
      item.requestedQuantity,
      item.quantity,
      item.volume,
      item.productQuantity,
    );
    const quantityValue =
      quantity && typeof quantity === "object"
        ? getValue(quantity.amount, quantity.value, quantity.quantity)
        : quantity;
    const unit =
      (quantity && typeof quantity === "object" ? quantity.unit : undefined) ||
      requirement.quantityUnit ||
      requirement.unit ||
      item.quantityUnit ||
      item.unit ||
      "";
    const name =
      getValue(
        product?.name,
        product?.productName,
        product?.title,
        requirement.productName,
        item.productName,
        item.productTitle,
      ) ||
      getLabel(product, ["name", "productName", "title", "label"]) ||
      (typeof item.commodity === "string" ? item.commodity : "") ||
      "—";

    return {
      id: key,
      name: String(name),
      sectorName: String(sectorName),
      image: getImageUrl(
        getValue(
          product?.mainImage,
          product?.image,
          requirement.mainImage,
          item.productImage,
          item.image,
          item.mainImage,
        ),
      ),
      quantity:
        quantityValue === undefined || quantityValue === null || quantityValue === ""
          ? "—"
          : String(quantityValue) + (unit ? " " + unit : ""),
    };
  });

  const statusValue =
    item.vendorResponse?.status || item.status || item.inquiryStatus || "";
  const status = getLabel(statusValue, ["name", "label", "value"]) || statusValue;
  const normalizedStatus = normalizeStatus(status);
  const statusClasses = {
    new: "new",
    pending: "pending",
    quoted: "quoted",
    under_review: "under-review",
    negotiation: "negotiation",
    converted: "converted",
    closed: "closed",
    accepted: "accepted",
    completed: "completed",
    cancelled: "cancelled",
    rejected: "cancelled",
    reject: "cancelled",
    processing: "processing",
  };
  const client = item.client || item.clientId || item.buyer || item.customer || item.user;

  return {
    id: inquiryDatabaseId,
    inquiryId: displayInquiryId,
    products,
    title: products[0]?.name || "—",
    image: products[0]?.image || "/assets/inquiries-icon.svg",
    seller:
      getValue(
        item.clientName,
        item.buyerName,
        item.customerName,
        item.companyName,
        item.seller,
        getLabel(client, ["companyName", "name", "username", "email"]),
      ) || "—",
    commodity: products.map((product) => product.name).join(", "),
    quantity: products[0]?.quantity || "—",
    status: String(status || "Unknown"),
    statusClass: statusClasses[normalizedStatus] || "new",
  };
};
const baseStatusOptions = [
  { value: "pending", label: "Pending" },
  { value: "reject", label: "Rejected" },
  { value: "complete", label: "Completed" },
];

const readCount = (summary, keys, fallback) => {
  const value = getValue(
    ...keys.flatMap((key) => [
      summary && summary[key],
      summary && summary[key + "Count"],
      summary && summary[key + "_count"],
    ]),
  );
  const count = Number(value);
  return Number.isFinite(count) ? count : fallback;
};

export default function Inquiries() {
  const { t, text, status: translateStatus } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    items = [],
    pagination = {},
    summary = {},
    loading = false,
    error = "",
  } = useSelector((state) => state.inquiry || {});
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef(null);
  const [page, setPage] = useState(1);
  const limit = 10;

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!statusDropdownRef.current?.contains(event.target)) {
        setStatusDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    dispatch(getInquiryListAction({
      page,
      limit,
      status: statusFilter,
      search: debouncedSearch,
    }));
  }, [dispatch, page, limit, statusFilter, debouncedSearch]);

  const inquiries = useMemo(
    () => (Array.isArray(items) ? items.map((item, index) => getInquiryRow(item, index)) : []),
    [items],
  );
  const filteredInquiries = useMemo(() => {
    const searchValue = search.toLowerCase().trim();
    return inquiries.filter((item) => {
      const matchesSearch =
        !searchValue ||
        [item.title, item.inquiryId, item.seller, item.commodity,
          ...item.products.map((product) => product.sectorName)]
          .join(" ")
          .toLowerCase()
          .includes(searchValue);
      const matchesStatus =
        !statusFilter || getStatusFilterValue(item.status) === getStatusFilterValue(statusFilter);
      return matchesSearch && matchesStatus;
    });
  }, [inquiries, search, statusFilter]);

  const statusOptions = useMemo(() => {
    const options = [...baseStatusOptions];
    const knownValues = new Set(options.map((option) => option.value));
    inquiries.forEach((item) => {
      const value = getStatusFilterValue(item.status);
      if (value && !knownValues.has(value)) {
        options.push({ value, label: formatStatus(value) });
        knownValues.add(value);
      }
    });
    return options;
  }, [inquiries]);

  const pendingCount = inquiries.filter(
    (item) => normalizeStatus(item.status) === "pending",
  ).length;
  const rejectedCount = inquiries.filter((item) =>
    ["rejected", "reject"].includes(normalizeStatus(item.status)),
  ).length;
  const completedCount = inquiries.filter(
    (item) => normalizeStatus(item.status) === "completed",
  ).length;
  const total = pagination.total !== undefined && pagination.total !== null
    ? Number(pagination.total)
    : inquiries.length;
  const stats = [
    {
      title: "Total",
      value: readCount(summary, ["total", "totalInquiries"], total),
      change: "All inquiries",
      icon: "/assets/inquiries-icon.svg",
      color: "gold",
    },
    {
      title: "Pending",
      value: readCount(summary, ["pending", "pendingInquiries"], pendingCount),
      change: "On this page",
      icon: "/assets/inquiries-icon.svg",
      color: "blue",
    },
    {
      title: "Reject",
      value: readCount(summary, ["rejected", "reject", "rejectedInquiries"], rejectedCount),
      change: "On this page",
      icon: "/assets/inquiries-icon.svg",
      color: "red",
    },
    {
      title: "Completed",
      value: readCount(summary, ["completed", "completedInquiries"], completedCount),
      change: "On this page",
      icon: "/assets/inquiries-icon.svg",
      color: "green",
    },
  ];

  const currentPage = Number(pagination.page) || page;
  const pageSize = Number(pagination.limit) || limit;
  const totalPages = Math.max(Number(pagination.totalPages) || 1, 1);
  const firstItem = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const lastItem = total === 0 ? 0 : Math.min(currentPage * pageSize, total);
  const pageNumbers = useMemo(() => {
    const start = Math.max(1, Math.min(currentPage - 1, totalPages - 3));
    const end = Math.min(totalPages, start + 3);
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }, [currentPage, totalPages]);

  return (
    <div className="dashboard-page inquiries-page">
      <div className="inquiries-page-header">
        <div>
          <h1 className="f-s-28 l-h-32 fw-semibold">{t("ui.received.inquiries")}</h1>
          <p className="f-s-14 l-h-20">{t("ui.track.and.manage.all.your.inquiries.with.sellers.in.one.place")}</p>
        </div>
      </div>
      <div className="inquiry-summary-grid">
        {stats.map((stat, index) => (
           <div className="overview-stat-card" key={stat.title}>
              <div className={`overview-stat-icon ${stat.color}`}>
                <span
                  className={`overview-stat-icon-img ${stat.color}`}
                  style={{
                    "--icon-url": `url(${stat.icon})`,
                  }}
                />
              </div>

              <div className="overview-stat-content">
                <span className="overview-stat-title">{stat.title}</span>

                <div className="overview-stat-bottom">
                  <strong>{stat.value}</strong>
                  <small className={stat.color}>{stat.change}</small>
                </div>
              </div>
            </div>
        ))}
      </div>
      <div className="inquiries-table-card">
        <div className="inquiries-table-header">
          <div>
            <h2>{t("ui.inquiry.details")}</h2>
            <p>{" "}{t("ui.view.and.manage.your.recent.inquiries")}{" "}</p>
          </div>
          <div className="inquiries-table-actions">
            <div className="inquiries-search">
              <img src="/assets/search-icon.svg" alt="" />
              <input
                type="text"
                placeholder={t("ui.search.inquiries")}
                value={search}
                onChange={(event) => { setSearch(event.target.value); setPage(1); }}
              />
              {search && (
                <button type="button" onClick={() => { setSearch(""); setPage(1); }}>
                  {"\u00D7"}
                </button>
              )}
            </div>
            <div
              className={"inquiries-status-filter" + (statusDropdownOpen ? " is-open" : "")}
              ref={statusDropdownRef}
            >
              <button
                type="button"
                className="inquiries-status-trigger"
                aria-label={t("ui.filter.inquiries.by.status")}
                aria-haspopup="listbox"
                aria-expanded={statusDropdownOpen}
                onClick={() => setStatusDropdownOpen((open) => !open)}
              >
                <span>
                      {statusFilter
                    ? translateStatus(statusOptions.find((option) => option.value === statusFilter)?.label ||
                      formatStatus(statusFilter))
                    : t("ui.allStatus")}
                </span>
                <svg viewBox="0 0 14 14" aria-hidden="true">
                  <path d="m3.5 5.25 3.5 3.5 3.5-3.5" />
                </svg>
              </button>
              {statusDropdownOpen && (
                <div
                  className="inquiries-status-menu"
                  role="listbox"
                  aria-label={t("ui.filter.inquiries.by.status")}
                >
                      {[{ value: "", label: t("ui.allStatus") }, ...statusOptions].map((option) => (
                    <button
                      type="button"
                      key={option.value || "all"}
                      className={"inquiries-status-option" + (statusFilter === option.value ? " is-selected" : "")}
                      role="option"
                      aria-selected={statusFilter === option.value}
                      onClick={() => {
                        setStatusFilter(option.value);
                        setPage(1);
                        setStatusDropdownOpen(false);
                      }}
                    >
                      {translateStatus(option.label)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="inquiries-table-wrapper">
          <table className="inquiries-modern-table">
            <thead>
              <tr>
                <th>{t("ui.product.meu6z3")}</th>
                <th>{t("ui.client")}</th>
                {/* <th>Commodity</th> */}
                <th>{t("ui.quantity")}</th>
                <th>{t("ui.status")}</th>
                <th className="inquiry-actions-heading">{t("ui.actions.8bdtct")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <DataTableLoadingRow colSpan={5} label={t("ui.loadingInquiries")} />
              ) : error ? (
                <tr>
                  <td colSpan="7">
                    <div className="inquiries-empty-state" role="alert">
                      <strong>{t("ui.unable.to.load.inquiries")}</strong>
                      <span>{text(error)}</span>
                    </div>
                  </td>
                </tr>
              ) : filteredInquiries.length > 0 ? (
                filteredInquiries.map((item) => (
                  <tr key={item.id}>
                    <td>
                      {item.products.map((product) => (
                        <div className="inquiry-product-cell" key={product.id}>
                          <div className="inquiry-product-image">
                            <img
                              src={product.image}
                              alt={product.name}
                              onError={(event) => {
                                event.currentTarget.style.display = "none";
                              }}
                            />
                          </div>
                          <div className="inquiry-product-info">
                            <strong>{product.name}</strong>
                            <span>{product.sectorName}</span>
                          </div>
                        </div>
                      ))}
                    </td>
                    <td>
                      <div className="inquiry-seller-cell">
                        <strong>{item.seller}</strong>
                      </div>
                    </td>
                    {/* <td>
                      <div className="inquiry-commodity-cell">
                        <div>
                          <strong>{item.commodity}</strong>
                        </div>
                      </div>
                    </td> */}
                    <td>
                      <div className="inquiry-quantity-cell">
                        {item.products.map((product) => (
                          <strong key={product.id}>{product.quantity}</strong>
                        ))}
                      </div>
                    </td>
                    <td>
                      <div className="inquiry-status-cell">
                        <span
                          className={`inquiry-status-badge ${item.statusClass}`}
                        >
                          {translateStatus(item.status)}
                        </span> 
                      </div>
                    </td>
                    <td>
                      <div className="inquiry-row-actions">
                        <button
                          type="button"
                          className="inquiry-view-btn"
                          onClick={() => navigate(`/inquiries/${item.id}`)}
                        >
                          <img src="/assets/eye.svg" alt="" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7">
                    <div className="inquiries-empty-state">
                      <svg
                        className="empty-search-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <circle cx="10.8" cy="10.8" r="6.8" stroke="currentColor" strokeWidth="1.7" />
                        <path d="m16 16 4.2 4.2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                      </svg>

                      <strong>{t("ui.no.inquiries.found")}</strong>

                      <span>{t("ui.try.changing.your.filters.or.search")}</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="inquiries-table-footer">
          <span>{t("ui.showing")}{" "}{firstItem}{" "}{t("ui.to")}{" "}{lastItem}{" "}{t("ui.of")}{" "}<strong>{total}</strong>{" "}{t("ui.inquiries")}</span>
          <div className="inquiries-pagination">
            <button
              type="button"
              onClick={() => setPage((current) => Math.max(current - 1, 1))}
              disabled={currentPage <= 1 || loading}
              aria-label={t("ui.previous.page")}
            >
              {"\u2039"}
            </button>
            {pageNumbers.map((pageNumber) => (
              <button
                type="button"
                key={pageNumber}
                className={pageNumber === currentPage ? "active" : ""}
                onClick={() => setPage(pageNumber)}
                disabled={loading}
              >
                {pageNumber}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPage((current) => Math.min(current + 1, totalPages))}
              disabled={currentPage >= totalPages || loading}
              aria-label={t("ui.next.page")}
            >
              {"\u203A"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
